import React, { useState, useEffect } from 'react';
import { ChevronRight, Scale, Activity, Target, ArrowLeft, Send, Loader2, Anchor, Waves } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BRAND_COLORS = {
  fliteboard: { primary: '#00D4FF', bg: '#001a22', border: '#00D4FF30', gradient: 'from-[#00D4FF]/10 to-transparent' },
  jetwave: { primary: '#FF6B00', bg: '#221000', border: '#FF6B0030', gradient: 'from-[#FF6B00]/10 to-transparent' },
  aerofoils: { primary: '#00E01A', bg: '#001a05', border: '#00E01A30', gradient: 'from-[#00E01A]/10 to-transparent' },
};
const BRAND_LABELS = { fliteboard: 'Fliteboard', jetwave: 'Jetwave', aerofoils: 'Aerofoils' };
const COMP_LABELS = { planche: 'Planche', moteur: 'Moteur', batterie: 'Batterie', chargeur: 'Chargeur', aile: 'Aile' };
const STEP_ICONS = [Scale, Activity, Target, Anchor];
const STEP_LABELS = ['Gabarit', 'Niveau', 'Utilisation', 'Lieu'];

export default function ConfigurateurProfil() {
  const [options, setOptions] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ weight: '', level: '', usage: '', location: '' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ first_name: '', last_name: '', email: '', phone: '', message: '' });
  const [quoteBrand, setQuoteBrand] = useState(null);
  const [quoteSent, setQuoteSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/configurateur-profil/options`).then(r => r.json()).then(setOptions).catch(console.error);
  }, []);

  const handleSelect = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    if (field === 'weight') setStep(1);
    else if (field === 'level') setStep(2);
    else if (field === 'usage') setStep(3);
    else if (field === 'location') submitRecommendation(updated);
  };

  const submitRecommendation = async (data) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/configurateur-profil/recommend`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
      });
      setResults(await res.json());
      setStep(4);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleQuoteSubmit = async (brand) => {
    if (!quoteForm.first_name || !quoteForm.email) { return; }
    setSending(true);
    const rec = results.recommendations[brand];
    const configLines = Object.entries(rec).filter(([k]) => !['price', 'photo'].includes(k))
      .map(([k, v]) => typeof v === 'object' ? `${COMP_LABELS[k] || k}: ${v.name || '-'}` : '').filter(Boolean);

    try {
      await fetch(`${API_URL}/api/configurateur-profil/quote-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quoteForm, brand, usage: form.usage, weight: form.weight, level: form.level, location: form.location,
          config_summary: configLines.join('\n'),
        }),
      });
      setQuoteSent(true);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const reset = () => {
    setStep(0); setForm({ weight: '', level: '', usage: '', location: '' }); setResults(null);
    setQuoteSent(false); setQuoteBrand(null);
    setQuoteForm({ first_name: '', last_name: '', email: '', phone: '', message: '' });
  };

  if (!options) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 size={32} className="text-[#00E01A] animate-spin" /></div>;

  const filteredUsages = step >= 2 ? (options.usages || []).filter(u => u.level === form.level) : [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="configurateur-profil">
      {/* Header */}
      <div className="bg-[#111] border-b border-[#222]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00E01A]/10 flex items-center justify-center"><Target size={22} className="text-[#00E01A]" /></div>
            <div>
              <h1 className="text-xl font-bold tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.08em' }}>Trouvez votre eFoil ideal</h1>
              <p className="text-[#8C8F94] text-sm">Riding Watt — Revendeur exclusif</p>
            </div>
          </div>
          {step < 4 && (
            <div className="flex items-center gap-2 mt-5">
              {[0, 1, 2, 3].map(i => {
                const Icon = STEP_ICONS[i]; const active = step === i; const done = step > i;
                return (
                  <React.Fragment key={i}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active ? 'bg-[#00E01A]/15 text-[#00E01A] border border-[#00E01A]/30' : done ? 'bg-[#142915] text-[#00E01A]/60' : 'bg-[#1a1a1a] text-[#555]'}`}>
                      <Icon size={14} /><span>{STEP_LABELS[i]}</span>
                    </div>
                    {i < 3 && <ChevronRight size={14} className="text-[#333]" />}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading && <div className="flex flex-col items-center py-20"><Loader2 size={40} className="text-[#00E01A] animate-spin mb-4" /><p className="text-[#8C8F94]">Analyse de votre profil...</p></div>}

        {/* Step 0: Weight */}
        {!loading && step === 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Quel est votre gabarit ?</h2>
            <p className="text-[#8C8F94] mb-6">Le poids du rider est le critere principal.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {options.weights.map(w => (
                <button key={w.value} onClick={() => handleSelect('weight', w.value)}
                  className="bg-[#111] border border-[#222] rounded-xl p-6 text-center hover:border-[#00E01A]/50 hover:bg-[#00E01A]/5 transition-all group" data-testid={`weight-${w.value}`}>
                  <Scale size={28} className="mx-auto text-[#555] group-hover:text-[#00E01A] transition-colors mb-3" />
                  <p className="text-white font-semibold">{w.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Level */}
        {!loading && step === 1 && (
          <div>
            <button onClick={() => setStep(0)} className="flex items-center gap-1 text-[#8C8F94] text-sm mb-4 hover:text-white"><ArrowLeft size={14} /> Retour</button>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Quel est votre niveau ?</h2>
            <p className="text-[#8C8F94] mb-6">Pour proposer la planche adaptee.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {options.levels.map(l => (
                <button key={l.value} onClick={() => handleSelect('level', l.value)}
                  className="bg-[#111] border border-[#222] rounded-xl p-6 text-center hover:border-[#00E01A]/50 hover:bg-[#00E01A]/5 transition-all group" data-testid={`level-${l.value}`}>
                  <Activity size={28} className="mx-auto text-[#555] group-hover:text-[#00E01A] transition-colors mb-3" />
                  <p className="text-white font-semibold text-lg">{l.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Usage */}
        {!loading && step === 2 && (
          <div>
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-[#8C8F94] text-sm mb-4 hover:text-white"><ArrowLeft size={14} /> Retour</button>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Quel type d'utilisation ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredUsages.map(u => (
                <button key={u.value} onClick={() => handleSelect('usage', u.value)}
                  className="bg-[#111] border border-[#222] rounded-xl p-6 text-left hover:border-[#00E01A]/50 hover:bg-[#00E01A]/5 transition-all group" data-testid={`usage-${u.value}`}>
                  <Target size={24} className="text-[#555] group-hover:text-[#00E01A] transition-colors mb-2" />
                  <p className="text-white font-semibold text-lg">{u.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Location */}
        {!loading && step === 3 && (
          <div>
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-[#8C8F94] text-sm mb-4 hover:text-white"><ArrowLeft size={14} /> Retour</button>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Ou allez-vous l'utiliser ?</h2>
            <p className="text-[#8C8F94] mb-6">Le lieu d'utilisation influence le type de planche recommande.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(options.locations || []).map(l => (
                <button key={l.value} onClick={() => handleSelect('location', l.value)}
                  className="bg-[#111] border border-[#222] rounded-xl p-8 text-center hover:border-[#00E01A]/50 hover:bg-[#00E01A]/5 transition-all group" data-testid={`location-${l.value}`}>
                  {l.value === 'plage' ? (
                    <Waves size={32} className="mx-auto text-[#555] group-hover:text-[#00E01A] transition-colors mb-3" />
                  ) : (
                    <Anchor size={32} className="mx-auto text-[#555] group-hover:text-[#00E01A] transition-colors mb-3" />
                  )}
                  <p className="text-white font-semibold text-lg">{l.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {!loading && step === 4 && results && (
          <div>
            <button onClick={reset} className="flex items-center gap-1 text-[#8C8F94] text-sm mb-4 hover:text-white"><ArrowLeft size={14} /> Recommencer</button>
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Nos recommandations pour vous</h2>
            <p className="text-[#8C8F94] mb-6">{results.profile.weight} — {results.profile.level} — {results.profile.usage} — {results.profile.location}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
              {['fliteboard', 'jetwave', 'aerofoils'].map(brand => {
                const rec = results.recommendations[brand];
                if (!rec) return null;
                const colors = BRAND_COLORS[brand];
                const planche = rec.planche || {};
                const price = rec.price;
                const photo = rec.photo;
                return (
                  <div key={brand} className={`rounded-2xl border overflow-hidden bg-gradient-to-b ${colors.gradient}`}
                    style={{ borderColor: colors.border, backgroundColor: colors.bg }} data-testid={`rec-${brand}`}>
                    {/* Header */}
                    <div className="p-4 border-b" style={{ borderColor: colors.border }}>
                      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: colors.primary }}>{BRAND_LABELS[brand]}</p>
                    </div>

                    {/* Photo */}
                    {photo && (
                      <div className="px-4 pt-4">
                        <img src={`${API_URL}${photo}`} alt={planche.name || brand} className="w-full h-40 object-contain rounded-lg bg-[#111]" />
                      </div>
                    )}

                    {/* Components */}
                    <div className="p-4 space-y-2.5">
                      {['planche', 'moteur', 'batterie', 'chargeur', 'aile'].map(ct => {
                        const comp = rec[ct];
                        if (!comp || typeof comp !== 'object') return null;
                        return (
                          <div key={ct} className="flex justify-between items-start">
                            <p className="text-[10px] uppercase tracking-widest text-[#555]">{COMP_LABELS[ct]}</p>
                            <p className="text-white text-sm font-semibold text-right">{comp.name || '-'}</p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Price */}
                    {price && (
                      <div className="px-4 pb-2">
                        <div className="text-right">
                          <p className="text-[10px] text-[#555] uppercase">A partir de</p>
                          <p className="text-2xl font-bold" style={{ color: colors.primary, fontFamily: "'Bebas Neue', sans-serif" }}>
                            {price.toLocaleString('fr-FR')} EUR
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Quote button */}
                    <div className="p-4 border-t" style={{ borderColor: colors.border }}>
                      <button onClick={() => { setQuoteBrand(brand); setQuoteSent(false); }}
                        className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all"
                        style={{ backgroundColor: colors.primary + '20', color: colors.primary, border: `1px solid ${colors.primary}40` }}
                        data-testid={`quote-btn-${brand}`}>
                        Demander un devis
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quote form */}
            {quoteBrand && !quoteSent && (
              <div className="bg-[#111] border border-[#222] rounded-2xl p-6 mb-6" data-testid="quote-form">
                <h3 className="text-white font-bold text-lg mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  Demande de devis — {BRAND_LABELS[quoteBrand]}
                </h3>
                <p className="text-[#8C8F94] text-sm mb-4">Remplissez vos coordonnees et nous vous recontacterons.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <input className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#00E01A] focus:outline-none"
                    placeholder="Prenom *" value={quoteForm.first_name} onChange={e => setQuoteForm(f => ({ ...f, first_name: e.target.value }))} data-testid="q-firstname" />
                  <input className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#00E01A] focus:outline-none"
                    placeholder="Nom" value={quoteForm.last_name} onChange={e => setQuoteForm(f => ({ ...f, last_name: e.target.value }))} />
                  <input className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#00E01A] focus:outline-none"
                    placeholder="Email *" type="email" value={quoteForm.email} onChange={e => setQuoteForm(f => ({ ...f, email: e.target.value }))} data-testid="q-email" />
                  <input className="bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#00E01A] focus:outline-none"
                    placeholder="Telephone" value={quoteForm.phone} onChange={e => setQuoteForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <textarea className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white text-sm placeholder:text-[#555] focus:border-[#00E01A] focus:outline-none h-20 resize-none mb-4"
                  placeholder="Message (optionnel)" value={quoteForm.message} onChange={e => setQuoteForm(f => ({ ...f, message: e.target.value }))} />
                <div className="flex gap-3">
                  <button onClick={() => setQuoteBrand(null)} className="px-4 py-2.5 text-[#8C8F94] text-sm">Annuler</button>
                  <button onClick={() => handleQuoteSubmit(quoteBrand)} disabled={sending || !quoteForm.first_name || !quoteForm.email}
                    className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-bold px-6 py-2.5 rounded-full transition-all shadow-[0_0_20px_rgba(0,224,26,0.3)] disabled:opacity-50"
                    data-testid="q-submit">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer ma demande
                  </button>
                </div>
              </div>
            )}

            {quoteSent && (
              <div className="bg-[#0d1a0d] border border-[#00E01A]/20 rounded-2xl p-8 text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-[#00E01A]/10 flex items-center justify-center mx-auto mb-4">
                  <Send size={28} className="text-[#00E01A]" />
                </div>
                <h3 className="text-[#00E01A] font-bold text-xl mb-2">Demande envoyee !</h3>
                <p className="text-[#8C8F94]">Nous vous recontacterons rapidement avec un devis personnalise.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
